import {ReactNode, useEffect, useLayoutEffect, useReducer, useRef, useState} from "react";
import {TurnContext, TurnProviderReturn} from "./TurnHooks";
import {useCombatants} from "@/encounter/combatants/CombatantHooks.ts";
import {useEncounter} from "@/encounter/EncounterHooks.ts";
import Combatant from "@/encounter/model/Combatant.ts";
import {applyCombatCommand, CombatCommand, getTurnState, saveTurnState, TurnState} from "@/api/TurnApi.ts";
import {useTurnRealtime} from "@/encounter/turns/useTurnRealtime.ts";
import {canActInRound, combatTokens, firstTurnInRound, navigateTurn, participatesInRound, roundCombatants, turnOrder} from "./TurnNavigation.ts";
import {notifyError} from "@/api/ErrorNotification.ts";
import {isAxiosError} from "axios";

export function TurnProvider({children}: {children: ReactNode}) {
    const encounter = useEncounter();
    const {subscribeToUpdates, onConnected} = useTurnRealtime(encounter.id);
    const {combatants} = useCombatants();
    const [turn, setTurn] = useReducer((_state: TurnState, next: TurnState) => next, {
        activeCombatantId: encounter.activeCombatantId, round: encounter.round, revision: -1, turnOrder: [], phase: "IDLE", participants: [],
    });
    const current = useRef(turn);
    const actors = useRef(combatants);
    useLayoutEffect(() => {actors.current = combatants;}, [combatants]);
    const scope = useRef(0);
    const queue = useRef<Promise<void>>(Promise.resolve());
    const [initialLoad, setInitialLoad] = useState<{encounterId?: string; error: boolean}>({error: false});
    const initialLoading = initialLoad.encounterId !== encounter.id;

    const accept = (state: TurnState) => {
        if (state.revision < current.current.revision) return;
        current.current = state;
        setTurn(state);
    };

    useEffect(() => {
        const initialState: TurnState = {
            activeCombatantId: encounter.activeCombatantId, round: encounter.round, revision: -1, turnOrder: [], phase: "IDLE", participants: [],
        };
        current.current = initialState;
        setTurn(initialState);
        queue.current = Promise.resolve();
        const generation = ++scope.current;
        const unsubscribe = subscribeToUpdates(state => {
            if (scope.current !== generation) return;
            accept(state);
            setInitialLoad({encounterId: encounter.id, error: false});
        });
        const refresh = () => {void getTurnState(encounter).then(state => {
            if (scope.current !== generation) return;
            accept(state);
            setInitialLoad({encounterId: encounter.id, error: false});
        }).catch(() => {
            if (scope.current === generation && current.current.revision < 0) {
                setInitialLoad({encounterId: encounter.id, error: true});
            }
        });};
        const unsubscribeConnection = onConnected(refresh);
        refresh();
        return () => {scope.current = generation + 1; unsubscribe(); unsubscribeConnection();};
    }, [encounter, subscribeToUpdates, onConnected]);

    const enqueue = async (action: () => Promise<void>) => {
        const generation = scope.current;
        const task = queue.current.then(async () => {
            if (scope.current !== generation || current.current.revision < 0) throw new Error("Kampfansicht wurde gewechselt oder ist noch nicht bereit.");
            await action();
        });
        // A failed operation cancels actions already queued against the same state.
        queue.current = task;
        void task.catch(() => {
            if (queue.current === task) queue.current = Promise.resolve();
        });
        try {
            return await task;
        } catch (error) {
            if (scope.current === generation) notifyError(error, "Die Kampfaktion konnte nicht gespeichert werden.");
            throw error;
        }
    };

    const updateTurn = async (values: {round: number; activeCombatantId?: string; turnOrder: string[]}) => {
        const generation = scope.current;
        try {
            const saved = await saveTurnState(encounter, {...values, expectedRevision: current.current.revision});
            if (scope.current === generation) accept(saved);
        } catch (error) {
            if (isAxiosError(error) && error.response?.status === 409 && scope.current === generation) {
                // Refresh a conflict without replaying the action against a different turn.
                const latest = await getTurnState(encounter);
                if (scope.current === generation) accept(latest);
            }
            throw error;
        }
    };

    const calculateTurn = (direction: 1 | -1) => enqueue(async () => {
        const next = navigateTurn(current.current, actors.current, direction);
        if (next) await updateTurn(next);
    });
    const runCombatCommand = (command: Omit<CombatCommand, "expectedRevision">) => {
        // The payload was prepared from this rendered state, not from a later realtime update.
        const expectedRevision = turn.revision;
        return enqueue(async () => {
        const generation = scope.current;
        try {
            const result = await applyCombatCommand(encounter, {...command, expectedRevision});
            if (scope.current !== generation || result.state.revision < current.current.revision) return;
            accept(result.state);
        } catch (error) {
            if (isAxiosError(error) && error.response?.status === 409 && scope.current === generation) {
                const latest = await getTurnState(encounter);
                if (scope.current === generation) accept(latest);
            }
            throw error;
        }
        });
    };
    const startCombat = () => runCombatCommand({action: "START_COMBAT"});
    const resetCombat = () => runCombatCommand({action: "RESET_COMBAT"});
    const setActiveCombatant = (actor: Combatant | undefined) => enqueue(async () => {
        const state = current.current;
        if (state.round <= 0 || actor && !canActInRound(state, actor, state.round)) return;
        await updateTurn({round: state.round, activeCombatantId: actor?.id, turnOrder: turnOrder(state, actors.current)});
    });
    const setRound = (round: number) => enqueue(async () => {
        if (!Number.isInteger(round) || round < 0) return;
        const state = current.current;
        if (round === state.round) return;
        if (round === 0) await updateTurn({round: 0, activeCombatantId: undefined, turnOrder: []});
        else {
            const next = firstTurnInRound(state, actors.current, round);
            if (next) await updateTurn(next);
        }
    });
    const byId = new Map(combatTokens(turn, combatants).map(actor => [actor.id, actor]));
    const storedOrder = turnOrder(turn, combatants);
    const cursorIndex = storedOrder.indexOf(turn.activeCombatantId ?? "");
    const remainingCombatants = storedOrder.slice(Math.max(0, cursorIndex))
        .flatMap(id => byId.has(id) && participatesInRound(turn, id, turn.round) ? [byId.get(id)!] : []);
    // Keep fallen participants visible in their round's slots while navigation skips them.
    const initiativeCombatants = turnOrder(turn, combatants).flatMap(id => byId.has(id) && participatesInRound(turn, id, turn.round) ? [byId.get(id)!] : []);
    const actingCombatants = initiativeCombatants.filter(actor => canActInRound(turn, actor, turn.round));
    const nextRoundCombatants = roundCombatants(turn, combatants, turn.round + 1);
    const providerReturn: TurnProviderReturn = {
        combatState: turn, runCombatCommand,
        actingCombatants, initiativeCombatants, nextRoundCombatants, remainingCombatants, initialLoading,
        activeCombatantId: turn.activeCombatantId,
        activeCombatant: combatants.find(actor => actor.id === turn.activeCombatantId),
        isActiveCombatant: actor => actor.id === turn.activeCombatantId,
        setActiveCombatant, round: turn.round, setRound,
        undoTurn: () => calculateTurn(-1), advanceTurn: () => calculateTurn(1), startCombat, resetCombat,
    };
    if (initialLoad.error && initialLoad.encounterId === encounter.id) throw new Error("Kampfzustand konnte nicht geladen werden.");
    return <TurnContext.Provider value={providerReturn}>{children}</TurnContext.Provider>;
}
